import { cn } from '../lib/cn';
import {
  GROUP_CLASS,
  GROUP_TEXT_CLASS,
  statusMeta,
  type TStatusGroup,
  type TVocabulary,
} from '../status/dictionary';

const HATCH = 'repeating-linear-gradient(45deg, currentColor 0 1.5px, transparent 1.5px 4px)';

const Dot = ({ group }: { group: TStatusGroup }) => {
  if (group === 'indeterminate') {
    return (
      <span
        aria-hidden
        className={cn('h-2 w-4 shrink-0 rounded-control border border-current', GROUP_CLASS[group])}
        style={{ backgroundImage: HATCH }}
      />
    );
  }

  return (
    <span
      aria-hidden
      className={cn(
        'size-dot shrink-0 rounded-full bg-current',
        GROUP_CLASS[group],
        (group === 'motion' || group === 'retrying') &&
          'animate-[hp-pulse_1.8s_ease-in-out_infinite]',
      )}
    />
  );
};

interface IStatusBadgeProps {
  vocabulary: TVocabulary;
  value: string;
  attemptCount?: number;
  maxAttempts?: number;
  nextAttemptAt?: string | null;
  /** Overrides the label when the status alone would promise a delivery that is not coming. */
  held?: { label: string; meaning: string } | null;
  className?: string;
}

const retryHint = (nextAttemptAt?: string | null): string => {
  if (!nextAttemptAt) return 'Retrying';
  const deltaMs = new Date(nextAttemptAt).getTime() - Date.now();
  if (Number.isNaN(deltaMs)) return 'Retrying';
  if (deltaMs <= 0) return 'Retry overdue';

  const minutes = Math.round(deltaMs / 60_000);
  if (minutes < 1) return 'Retry in <1m';
  if (minutes < 60) return `Retry in ${minutes}m`;
  return `Retry in ${Math.round(minutes / 60)}h`;
};

export const StatusBadge = ({
  vocabulary,
  value,
  attemptCount,
  maxAttempts,
  nextAttemptAt,
  held,
  className,
}: IStatusBadgeProps) => {
  const meta = statusMeta(vocabulary, value);

  // A value the dictionary does not know is shown raw, never mapped to the nearest neighbour.
  if (!meta) {
    console.warn(`[status] unknown ${vocabulary} value: ${value}`);
    return (
      <span className={cn('inline-flex items-center gap-2 text-meta text-muted', className)}>
        <span aria-hidden className="size-dot shrink-0 rounded-full border border-current" />
        <span className="font-mono">{value}</span>
      </span>
    );
  }

  // A hold outranks the status word: the retry countdown is the one thing that must not run
  // while the flow it belongs to is claiming nothing.
  const group = held ? 'suppressed' : meta.group;
  const showRetry = !held && meta.group === 'retrying';

  return (
    <span
      className={cn('inline-flex items-center gap-2 text-meta', GROUP_TEXT_CLASS[group], className)}
      title={held ? held.meaning : meta.meaning}
    >
      <Dot group={group} />
      <span>{held ? held.label : showRetry ? retryHint(nextAttemptAt) : meta.label}</span>
      {showRetry && attemptCount !== undefined && maxAttempts !== undefined && (
        <span className="font-mono text-muted">
          {attemptCount}/{maxAttempts}
        </span>
      )}
    </span>
  );
};
